import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Switch } from "@vaiu/ui/switch";
import { Badge } from "@vaiu/ui/badge";
import { ArrowLeft, Cookie, Shield, Users, BarChart3, Target, Info, Trash2, RefreshCw } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useCookieConsent } from "@/context/CookieConsentContext";

/**
 * Interactive Cookie Settings Management Page
 * 
 * This page provides users with granular control over their cookie preferences:
 * - Category-specific enable/disable toggles
 * - Real-time preference updates with visual feedback
 * - Clear descriptions and examples for each category
 * - Reset functionality with confirmation
 * - Privacy rights information
 * - Links to related policies
 */
const CookieSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    consentGiven,
    updateConsent,
    clearAllCookies,
    resetConsent
  } = useCookieConsent();
  
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleConsentChange = async (category: keyof typeof consentGiven, enabled: boolean) => {
    if (category === 'essential') {
      // Essential cookies cannot be disabled
      return;
    }
    
    setIsUpdating(true);
    await updateConsent({ [category]: enabled });
    
    // Add a brief delay for visual feedback
    setTimeout(() => setIsUpdating(false), 500);
  };

  const handleResetAll = async () => {
    setIsUpdating(true);
    await resetConsent();
    setShowResetConfirm(false);
    setTimeout(() => setIsUpdating(false), 500);
  };

  const handleClearAll = async () => {
    setIsUpdating(true);
    await clearAllCookies();
    setShowClearConfirm(false);
    setTimeout(() => setIsUpdating(false), 500);
  };

  const cookieCategories = [
    {
      key: 'essential' as const,
      title: 'Essential Cookies',
      icon: Shield,
      description: 'These cookies are necessary for the website to function and cannot be switched off in our systems.',
      examples: ['Authentication', 'Security tokens', 'Session management', 'Language preferences'],
      color: 'green',
      required: true,
      details: 'Essential cookies are required for basic website functionality including login, security, and core features. These cannot be disabled as they are necessary for the platform to operate safely and securely.'
    },
    {
      key: 'functional' as const,
      title: 'Functional Cookies',
      icon: Users,
      description: 'These cookies enable the website to provide enhanced functionality and personalization.',
      examples: ['UI preferences', 'Saved form data', 'Accessibility settings', 'Recent searches'],
      color: 'blue',
      required: false,
      details: 'Functional cookies remember your choices and provide enhanced, personalized features. Disabling these may limit some website functionality but won\'t affect core operations.'
    },
    {
      key: 'analytics' as const,
      title: 'Analytics Cookies',
      icon: BarChart3,
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: ['Google Analytics', 'Page views', 'User behavior', 'Performance metrics'],
      color: 'purple',
      required: false,
      details: 'Analytics cookies help us improve the website by understanding how users interact with different features. All data is collected anonymously and cannot be used to identify you personally.'
    },
    {
      key: 'marketing' as const,
      title: 'Marketing Cookies',
      icon: Target,
      description: 'These cookies track your activity to help show you relevant advertisements and measure campaign effectiveness.',
      examples: ['Ad targeting', 'Social media', 'Campaign tracking', 'Conversion metrics'],
      color: 'orange',
      required: false,
      details: 'Marketing cookies are used to track visitors across websites to display relevant advertisements. You can disable these without affecting core website functionality.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <Cookie className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cookie Settings</h1>
              <p className="text-gray-600">Manage your cookie preferences and privacy settings</p>
            </div>
          </div>
        </div>

        {/* Status Indicator */}
        {isUpdating && (
          <Card className="mb-6 border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                <span className="text-blue-800 font-medium">Updating your preferences...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cookie Categories */}
        <div className="space-y-6">
          {cookieCategories.map((category) => {
            const IconComponent = category.icon;
            const isEnabled = consentGiven[category.key];
            
            return (
              <Card key={category.key} className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <IconComponent className={`h-6 w-6 text-${category.color}-600 shrink-0`} />
                      <div className="min-w-0">
                        <CardTitle className="text-xl">{category.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {category.required && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Required
                            </Badge>
                          )}
                          <Badge 
                            variant={isEnabled ? "default" : "secondary"}
                            className={
                              isEnabled 
                                ? "bg-green-100 text-green-800 border-green-200" 
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }
                          >
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`inline-flex min-h-11 min-w-11 items-center justify-center shrink-0 ${
                        category.required || isUpdating ? '' : 'cursor-pointer touch-manipulation'
                      }`}
                      {...(!category.required && !isUpdating
                        ? {
                            role: 'button' as const,
                            tabIndex: 0,
                            onClick: () => handleConsentChange(category.key, !isEnabled),
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleConsentChange(category.key, !isEnabled);
                              }
                            },
                          }
                        : {})}
                    >
                      <Switch
                        id={`cookie-settings-${category.key}`}
                        checked={isEnabled}
                        onCheckedChange={(checked) => handleConsentChange(category.key, checked)}
                        disabled={category.required || isUpdating}
                        className="data-[state=checked]:bg-primary"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600">{category.description}</p>
                  
                  {/* Detailed explanation */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 mb-3">{category.details}</p>
                    <div>
                      <h4 className="font-medium text-sm mb-2">Examples of {category.title.toLowerCase()}:</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {category.examples.map((example, index) => (
                          <span key={index} className="text-xs text-gray-600 flex items-center gap-1">
                            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Special notice for required cookies */}
                  {category.required && (
                    <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-l-yellow-400">
                      <div className="flex items-center gap-2">
                        <Info className="h-4 w-4 text-yellow-600" />
                        <p className="text-sm text-yellow-800">
                          These cookies cannot be disabled as they are essential for website security and functionality.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Accept All */}
              <div className="space-y-3">
                <h4 className="font-medium">Accept All Cookies</h4>
                <p className="text-sm text-gray-600">
                  Enable all cookie categories for the best experience and personalized features.
                </p>
                <Button
                  onClick={() => updateConsent({
                    functional: true,
                    analytics: true,
                    marketing: true
                  })}
                  disabled={isUpdating}
                  className="w-full"
                >
                  Accept All
                </Button>
              </div>

              {/* Reject All */}
              <div className="space-y-3">
                <h4 className="font-medium">Reject All Optional</h4>
                <p className="text-sm text-gray-600">
                  Disable all optional cookies. Only essential cookies will remain active.
                </p>
                <Button
                  variant="outline"
                  onClick={() => updateConsent({
                    functional: false,
                    analytics: false,
                    marketing: false
                  })}
                  disabled={isUpdating}
                  className="w-full"
                >
                  Reject All Optional
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Options */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-red-700">Advanced Options</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Reset Preferences */}
              <div className="space-y-3">
                <h4 className="font-medium">Reset All Preferences</h4>
                <p className="text-sm text-gray-600">
                  Reset all cookie preferences to default settings. This will restore the initial 
                  cookie consent state.
                </p>
                {!showResetConfirm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowResetConfirm(true)}
                    disabled={isUpdating}
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset Preferences
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-red-600 font-medium">Are you sure?</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleResetAll}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Yes, Reset
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowResetConfirm(false)}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Clear All Cookies */}
              <div className="space-y-3">
                <h4 className="font-medium">Clear All Cookies</h4>
                <p className="text-sm text-gray-600">
                  Delete all cookies from our website. You will be logged out and will need to 
                  set your preferences again.
                </p>
                {!showClearConfirm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowClearConfirm(true)}
                    disabled={isUpdating}
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All Cookies
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-red-600 font-medium">This will log you out!</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleClearAll}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Yes, Clear
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowClearConfirm(false)}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Rights Information */}
        <Card className="mt-8 border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="text-blue-700">Your Privacy Rights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Under GDPR and other privacy laws, you have the following rights regarding your data and cookies:
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Data Rights:</h4>
                <ul className="space-y-1 text-gray-600">
                  <li>• Right to access your data</li>
                  <li>• Right to rectification</li>
                  <li>• Right to erasure (right to be forgotten)</li>
                  <li>• Right to restrict processing</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Cookie Rights:</h4>
                <ul className="space-y-1 text-gray-600">
                  <li>• Right to withdraw consent anytime</li>
                  <li>• Right to know what cookies are used</li>
                  <li>• Right to opt-out of non-essential cookies</li>
                  <li>• Right to data portability</li>
                </ul>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/privacy-policy">
                <Button variant="outline" size="sm">
                  Privacy Policy
                </Button>
              </Link>
              <Link to="/cookie-policy">
                <Button variant="outline" size="sm">
                  Cookie Policy
                </Button>
              </Link>
              <a href="mailto:privacy@vaiu.io" className="no-underline">
                <Button variant="outline" size="sm">
                  Contact Privacy Team
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>
            Your preferences are saved automatically and will be remembered for future visits.
            <br />
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsPage; 